import { useState, useEffect, useCallback, useRef } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, DEPARTMENTS, canAccess } from "@/lib/constants";
import api from "@/lib/api";
import { cn } from "@/lib/utils";

const DEPT_LABEL = { general: "General", ...Object.fromEntries(DEPARTMENTS.map((d) => [d.key, d.label])) };

function playNewEmailChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    [880, 1174].forEach((freq, idx) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "sine"; o.frequency.value = freq;
      const t = now + idx * 0.14;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.18, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
      o.start(t); o.stop(t + 0.32);
    });
    setTimeout(() => ctx.close().catch(() => {}), 800);
  } catch { /* ignore */ }
}
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { LOGO_URL } from "@/lib/brand";
import NotificationBell from "@/components/NotificationBell";
import InstallGuide from "@/components/InstallGuide";
import {
  SquaresFour,
  Users as UsersIcon,
  Buildings,
  UserGear,
  SignOut,
  Stack,
  GitBranch,
  ChartBar,
  ChartLineUp,
  TrendUp,
  DesktopTower,
  Wrench,
  Car,
  ArrowsLeftRight,
  Brain,
  Kanban,
  CalendarCheck,
  ChartPieSlice,
  ClipboardText,
  BookOpen,
  List as ListIcon,
  Factory,
  ClockCounterClockwise,
} from "@phosphor-icons/react";

import { Archive as ArchiveNav, Stack as StackNav, MagnifyingGlass as MagnifyingGlassNav } from "@phosphor-icons/react";

const NAV = [
  { to: "/", label: "Dashboard", icon: SquaresFour, end: true },
  { to: "/productie", label: "Producție Rezervoare", icon: StackNav, roles: ["pm", "admin"] },
  { to: "/arhiva", label: "Arhivă proiecte", icon: ArchiveNav, roles: ["pm", "admin"] },
  { to: "/leads", label: "Lead-uri", icon: UsersIcon },
  { to: "/pipeline", label: "Pipeline", icon: Kanban },
  { to: "/activities", label: "Activități", icon: CalendarCheck },
  { to: "/previziuni", label: "Previziuni", icon: TrendUp, roles: ["vanzari", "asistent_vanzari", "pm", "admin"] },
  { to: "/performance", label: "Performanță", icon: ChartPieSlice },
  { to: "/projects", label: "Proiecte", icon: Buildings },
  { to: "/cautare-documente", label: "Căutare documente", icon: MagnifyingGlassNav },
  { to: "/nomenclatoare", label: "Nomenclatoare", icon: StackNav },
  { to: "/coordonare", label: "Coordonare", icon: UsersIcon, coordinator: true },
  { to: "/concedii", label: "CO&Sărbători", icon: CalendarCheck },
  { to: "/parc-auto", label: "Parc Auto", icon: Car, roles: ["pm", "admin"] },
  { to: "/echipamente", label: "Echipamente", icon: Wrench, roles: ["pm", "admin"] },
  { to: "/carnet-service", label: "Carnet Service", icon: CalendarCheck, roles: ["pm", "admin"] },
  { to: "/visio-ai", label: "Visio AI", icon: Brain, roles: ["pm", "inginer", "admin"] },
  { to: "/exec", label: "Conducere", icon: ChartLineUp, roles: ["pm"] },
  { to: "/workflow", label: "Workflow", icon: GitBranch, adminOnly: true },
  { to: "/companion", label: "Companion", icon: DesktopTower },
  { to: "/manual", label: "Manual", icon: BookOpen },
  { to: "/users", label: "Utilizatori", icon: UserGear, adminOnly: true },
  { to: "/audit", label: "Jurnal acces", icon: ClockCounterClockwise, adminOnly: true },
];

function SidebarContent({ user, hasRole, onLogout, onNavigate, unread = {}, coordUnalloc = {} }) {
  const deptItems = DEPARTMENTS.filter((d) => hasRole("admin") || (user?.departments || []).includes(d.key));
  return (
    <>
      <div className="flex items-center justify-between px-5 py-5">
        <img src={LOGO_URL} alt="Highberg" className="h-9 w-auto max-w-[150px] object-contain object-left" style={{ mixBlendMode: "screen" }} />
        <span className="hidden lg:block"><NotificationBell /></span>
      </div>

      <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto px-3 py-2">
        {NAV.filter((n) => (n.coordinator ? (user?.role === "admin" || user?.is_coordinator || (user?.email || "").toLowerCase() === "andra.mihai@highbergsolution.ro") : canAccess(user, n.to))).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={onNavigate}
            data-testid={`nav-${n.label.toLowerCase()}`}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150",
                isActive
                  ? "bg-primary/15 text-primary"
                  : "text-muted-foreground hover:bg-white/5 hover:text-foreground"
              )
            }
          >
            <n.icon size={19} weight="bold" />
            {n.label}
          </NavLink>
        ))}
        {deptItems.length > 0 && (
          <div className="pt-3">
            <NavLink to="/departamente" onClick={onNavigate} data-testid="nav-dept-index" className="block px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70 hover:text-foreground">Departamente Highberg</NavLink>
            {deptItems.map((d) => (
              <NavLink key={d.key} to={`/departamente/${d.key}`} onClick={onNavigate} data-testid={`nav-dept-${d.key}`}
                className={({ isActive }) => cn("flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150", isActive ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-white/5 hover:text-foreground")}>
                <Factory size={18} weight="bold" /> <span className="flex-1 truncate">{d.label}</span>
                {user?.role !== "admin" && coordUnalloc[d.key] > 0 && <span className="min-w-[20px] rounded-full bg-amber-500/90 px-1.5 py-0.5 text-center text-[10px] font-bold text-black" title="Proiecte nealocate unui membru" data-testid={`nav-unalloc-${d.key}`}>{coordUnalloc[d.key]}</span>}
                {unread[d.key] > 0 && <span className="min-w-[20px] rounded-full bg-red-500/90 px-1.5 py-0.5 text-center text-[10px] font-bold text-white" data-testid={`nav-dept-badge-${d.key}`}>{unread[d.key]}</span>}
              </NavLink>
            ))}
          </div>
        )}
      </nav>

      <div className="border-t border-border p-3">
        <div className="flex items-center gap-3 rounded-lg px-2 py-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-violet-500/20 text-sm font-bold text-violet-300">
            {(user?.name || "?").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold">{user?.name}</div>
            <div className="text-[11px] text-muted-foreground">{ROLE_LABELS[user?.role]}</div>
          </div>
          <button
            onClick={onLogout}
            data-testid="logout-btn"
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-white/5 hover:text-red-400"
            title="Deconectare"
          >
            <SignOut size={18} weight="bold" />
          </button>
        </div>
      </div>
    </>
  );
}

export default function Layout() {
  const { user, logout, hasRole } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState({});
  const [coordUnalloc, setCoordUnalloc] = useState({});
  const prevCountsRef = useRef(null);

  const loadCounts = useCallback(() => {
    api.get("/inbox/unread-counts").then((r) => {
      const next = r.data?.counts || {};
      const prev = prevCountsRef.current;
      if (prev) {
        const grown = Object.keys(next).filter((k) => (next[k] || 0) > (prev[k] || 0));
        if (grown.length) {
          playNewEmailChime();
          const labels = grown.map((k) => DEPT_LABEL[k] || k).join(", ");
          toast(`📧 Email nou — ${labels}`, { description: "Ai corespondență nouă de tratat." });
        }
      }
      prevCountsRef.current = next;
      setUnread(next);
    }).catch(() => {});
    api.get("/coordinator/unallocated").then((r) => setCoordUnalloc(r.data?.counts || {})).catch(() => {});
  }, []);

  useEffect(() => {
    loadCounts();
    const t = setInterval(loadCounts, 30000);
    const h = () => setTimeout(loadCounts, 500);
    window.addEventListener("inbox-read", h);
    return () => { clearInterval(t); window.removeEventListener("inbox-read", h); };
  }, [loadCounts]);

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background text-foreground">
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-border bg-[#0d1017] lg:flex">
        <SidebarContent user={user} hasRole={hasRole} onLogout={doLogout} onNavigate={() => {}} unread={unread} coordUnalloc={coordUnalloc} />
      </aside>

      {/* Mobile drawer */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-64 border-border bg-[#0d1017] p-0">
          <div className="flex h-full flex-col">
            <SidebarContent user={user} hasRole={hasRole} onLogout={doLogout} onNavigate={() => setOpen(false)} unread={unread} coordUnalloc={coordUnalloc} />
          </div>
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile top bar */}
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-border bg-black/60 px-4 py-3 backdrop-blur-xl lg:hidden">
          <button
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-btn"
            className="rounded-md p-1.5 text-muted-foreground hover:bg-white/5 hover:text-foreground"
          >
            <ListIcon size={22} weight="bold" />
          </button>
          <img src={LOGO_URL} alt="Highberg" className="h-7 w-auto object-contain" style={{ mixBlendMode: "screen" }} />
          <div className="ml-auto"><NotificationBell /></div>
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <Outlet />
        </main>
      </div>
      <InstallGuide />
    </div>
  );
}
